import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { addDays, format } from 'date-fns'
import { useDispatch } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { useProject } from '@/Hooks/UseProject'
import { useProjection } from '@/Hooks/ProjectionProvider'
import { useDatePicker } from '@/Hooks/UseDatePicker'
import { getDefaultTaskDates, maxDate, parseLocalDate, safeFormat } from '@/Helpers/DateFunctions'

import { updateTaskAction } from '@/Redux/Actions/ProgrammationManagerActions'
import { updateTaskBaseDates, updateTaskScheduledDates } from '@/Services/ProgrammationManagerService'

import InputText from '../../components/InputText'
import BaseLineCreator from '@/Components/BaseLineCreator/BaseLineCreator'
import { getProjectBaselines, getProjectBaselineTasks, ProjectBaseline, selectProjectBaseline, updateProjectBaselineTask } from '@/Services/ProjectBaselineService'

const TaskBaseDates = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened: task, patchItemOpened, setEditingItem, mode } = useProgrammation()
  const { project } = useProject()
  const { projectedEndDate, extendedNonWorkingDays } = useProjection()

  const { showDateRangePicker, hideDatePicker } = useDatePicker()

  const [ baseDaysReset, setBaseDaysReset ] = useState(0)
  const [baselines, setBaselines] = useState<ProjectBaseline[]>([])
  const [selectedBaselineId, setSelectedBaselineId] = useState('')
  const [baselineTask, setBaselineTask] = useState<any>()
  const [showCreator, setShowCreator] = useState(false)

  useEffect(() => {
    if (!project._id) return
    getProjectBaselines(project._id).then(data => { setBaselines(data.baselines || []); setSelectedBaselineId(data.selectedBaseline || '') }).catch(() => undefined)
  }, [project._id])

  useEffect(() => {
    if (!project._id || !selectedBaselineId) { setBaselineTask(undefined); return }
    getProjectBaselineTasks(project._id, selectedBaselineId).then(rows => setBaselineTask(rows.find((row: any) => String(row.task) === String(task._id)))).catch(() => setBaselineTask(undefined))
  }, [project._id, selectedBaselineId, task._id])

  const multipleMode = baselines.length > 0
  const selectedBaseline = baselines.find(item => item._id === selectedBaselineId)
  const creatorAvatar = selectedBaseline?.createdBy?.avatar || selectedBaseline?.createdBy?.image
  const baseStart = safeFormat(multipleMode ? baselineTask?.startDate : (task.baseStart || task.startsAt), 'yyyy-MM-dd')
  const baseEnd = safeFormat(multipleMode ? baselineTask?.endDate : (task.baseEnd || task.endsAt), 'yyyy-MM-dd')

  const saveChanges = async (baseStart: string, baseEnd: string) => {
    setEditingItem(true)

    if (multipleMode && selectedBaselineId) {
      try {
        const data = await updateProjectBaselineTask(project._id, selectedBaselineId, task._id, { baseStart, baseEnd, startDate: baseStart, endDate: baseEnd })
        setBaselineTask(data)
        setEditingItem(false)
        return true
      } catch (_) {
        setEditingItem(false)
        return false
      }
    }

    const [ error, data ] = await updateTaskBaseDates(task._id, baseStart, baseEnd)

    setEditingItem(false)

    if (error) {
      return false
    }

    const newItemOpened = {
      ...task,
      baseStart,
      baseEnd,
      baseDays: data.task.baseDays,
    }

    patchItemOpened(newItemOpened)

    await dispatch(updateTaskAction(data.task, data.updatedTasks))

    if (mode === 'create') {
      const [ schedError, schedData ] = await updateTaskScheduledDates(task._id, baseStart, baseEnd)

      if (!schedError && schedData?.task) {
        patchItemOpened({
          ...newItemOpened,
          startsAt: baseStart,
          endsAt: baseEnd,
          executeDays: schedData.task.executeDays,
        })

        await dispatch(updateTaskAction(schedData.task, schedData.updatedTasks))
      }
    }
  }

  const handleChangeDates = async (dates: any) => {

    const {
      startDate,
      endDate
    } = dates

    await saveChanges(startDate, endDate)

    hideDatePicker()
  }

  const handleShowDateRangePicker = () => {

    if (task.hasSubTasks) {
      return
    }

    const defaultDates = getDefaultTaskDates(extendedNonWorkingDays)

    showDateRangePicker({
      startDate: baseStart || defaultDates.startDate,
      endDate: baseEnd || defaultDates.endDate,
      projectStartDate: project.startsAt,
      projectEndDate: maxDate(project.endsAt, projectedEndDate) || project.endsAt,
      nonWorkingDays: extendedNonWorkingDays,
      onChangeRange: handleChangeDates
    })
  }

  const handleChangeBaseDays = async (value: string) => {

    if (task.hasSubTasks) {
      return
    }

    const newBaseDays = parseInt(value, 10)

    if (isNaN(newBaseDays) || newBaseDays < 1) {
      setBaseDaysReset(t => t + 1)
      return
    }

    const parsedBaseStart = parseLocalDate(baseStart)

    if (!parsedBaseStart) {
      setBaseDaysReset(t => t + 1)
      return
    }

    const newBaseEnd = format(addDays(parsedBaseStart, newBaseDays - 1), 'yyyy-MM-dd')

    await saveChanges(baseStart, newBaseEnd)
  }

  return (
    <>
      <Row>
        <Col>
          <h5 className='group-title'>
            {t('PROGRAMMATION_MANAGER:BaseDatesTitleLabel')}
          </h5>
        </Col>
      </Row>
      <Row><Col><Form.Group><Form.Label>Linea base</Form.Label><div className="d-flex">{creatorAvatar && <img src={creatorAvatar} alt="" width={32} height={32} className="rounded-circle mr-1" />}<Form.Control as="select" value={selectedBaselineId} disabled={!multipleMode} onChange={async e => { setSelectedBaselineId(e.target.value); if (project.mySettings?._id) await selectProjectBaseline(project.mySettings._id, e.target.value) }}><option value="">Fechas base actuales</option>{baselines.map(item => <option key={item._id} value={item._id}>{item.name}</option>)}</Form.Control><button className="btn btn-secondary ml-1" onClick={() => setShowCreator(true)} type="button"><i className="fas fa-plus" /></button></div></Form.Group></Col></Row>
      <Row>

        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseStartLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(baseStart, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseEndLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(baseEnd, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseDaysLabel')}
            </Form.Label>
            <InputText
              key={`baseDays-${baseDaysReset}`}
              value={multipleMode ? (baselineTask?.durationMinutes ? Math.ceil(baselineTask.durationMinutes / 1440) : '') : (task.baseDays || task.executeDays)}
              onChange={handleChangeBaseDays}
              style={{
                padding: '0.375rem 0.75rem !important'
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <BaseLineCreator show={showCreator} projectId={project._id} taskCount={0} onHide={() => setShowCreator(false)} onCreated={baseline => { setBaselines(items => [...items, baseline]); setSelectedBaselineId(baseline._id); setShowCreator(false) }} />
    </>
  )
}

export default TaskBaseDates
